import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button } from '../ui';
import { MasteryMark } from './MasteryMark';
import { estimatedMinutes } from '../../context/MissionContext';

/**
 * MissionCard (spec §7.3, §10.3) — one mission in the Mission Center.
 *
 * Shows title, scenario teaser, origin, topic, estimated time, mastery mark
 * and status. Status rides on `data-status` and is *also* written out in a
 * badge, because a colour-only status would fail §12; the mastery mark carries
 * its own shape for the same reason.
 *
 * Navigation is delegated to `onStart` rather than baked in, so the Mission
 * Center, the onboarding path and any future embed all decide themselves where
 * a run begins.
 */

const STATUS_TONE = {
  due: 'signal',
  progress: 'brand',
  secure: 'success',
  new: undefined,
};

/**
 * @param {object} props
 * @param {object} props.mission mission DTO from `GET /api/missions`.
 * @param {'due'|'new'|'progress'|'secure'} [props.status='new']
 * @param {() => void} props.onStart
 * @param {2|3|4} [props.headingLevel=3]
 * @param {string} [props.className]
 */
export const MissionCard = forwardRef(function MissionCard(
  { mission, status = 'new', onStart, headingLevel = 3, className = '', ...rest },
  ref
) {
  const { t } = useTranslation();
  if (!mission) return null;

  const Heading = `h${headingLevel}`;
  const origin = mission.origin || 'generated';
  const minutes = estimatedMinutes(mission);
  const mastery = mission.mastery || { level: 'new', reasons: [] };
  const inProgress = status === 'progress';
  const actionLabel = inProgress ? t('missions.continue') : t('missions.start');
  const actionName = inProgress
    ? t('missions.continueNamed', { title: mission.title })
    : t('missions.startNamed', { title: mission.title });

  return (
    <article
      ref={ref}
      className={`etch-mission-card ${className}`.trim()}
      data-status={status}
      data-origin={origin}
      data-ai-generated="true"
      {...rest}
    >
      <div className="etch-mission-card__head">
        <span className="etch-eyebrow">{t([`missions.origin.${origin}`, 'missions.title'])}</span>
        {/* Art. 50 Abs. 1 AI Act: shown unconditionally for now, independent
            of `origin` — known to be too broad for Knowledge-Explorer-
            authored "einpraegen" missions, which come from a static i18n
            template, not an AI provider call. See the fuller note above the
            disclosure paragraph in MissionRunner.jsx for why this can't yet
            be narrowed correctly and what would need to change. The label
            hedges ("Möglicherweise" / "Possibly") instead of asserting AI
            authorship as settled fact. Mirrors the chat badge in
            Message.jsx (.etch-message__ai). */}
        <span className="etch-mission-card__ai" title={t('missions.aiBadgeLabel')}>
          <span aria-hidden="true">{t('missions.aiBadge')}</span>
          <span className="etch-visually-hidden">{t('missions.aiBadgeLabel')}</span>
        </span>
        <Badge tone={STATUS_TONE[status]}>{t([`missions.status.${status}`, 'states.empty'])}</Badge>
      </div>

      <Heading className="etch-mission-card__title">{mission.title}</Heading>

      {mission.scenario && <p className="etch-mission-card__teaser">{mission.scenario}</p>}

      <dl className="etch-mission-card__meta">
        {mission.topic && (
          <div className="etch-mission-card__meta-item">
            <dt>{t('missions.filterTopic')}</dt>
            <dd>{mission.topic}</dd>
          </div>
        )}
        <div className="etch-mission-card__meta-item">
          <dt>{t('missions.timeEstimate')}</dt>
          <dd>{t('missions.minutes', { count: minutes })}</dd>
        </div>
      </dl>

      <div className="etch-mission-card__footer">
        <MasteryMark level={mastery.level} reasons={mastery.reasons} size="compact" />
        <Button size="compact" onClick={onStart} aria-label={actionName}>
          {actionLabel}
        </Button>
      </div>
    </article>
  );
});

export default MissionCard;
